import * as Q from 'q';
import * as ko from 'knockout';
import * as _ from "underscore";
import * as localisationHelper from "localisationHelper";

import "knockoutValidation";

// Models
import { SelectTwoWithChildren } from "Content/ts/Models/Shared/SelectTwo/selectTwoModels";
import { IClubSelectionParams } from "Content/ts/Models/Shared/ClubSelector/IClubSelectionParams";
import { HomeClubConfigurationResponse } from "Content/ts/Models/Shared/ClubSelector/clubConfiguration";
import { ClubLocationSelectorResponse } from "Content/ts/Models/Shared/ClubSelector/multipleClubSelector";

// Configuration
import { ClubSelectionConfiguration } from "Content/ts/Shared/ClubSelector/Configuration/clubSelectionConfiguration";

// Services
import { ConfigurableTextService } from "Scripts/ts/Localisation/ConfigurableTextService";
import { ValidationService } from "Content/ts/Shared/Services/Validation/validationService";
import { ClubSelectionService } from "Content/ts/Shared/ClubSelector/Services/clubSelectionServices";
import { IClubSelectionCustomMessages } from 'Content/ts/Models/Shared/ClubSelector/ClubSelectionCustomMessages';

export const Deps = {
    LocalisationHelper: localisationHelper
}

export class ClubSelectionBase {
    customMessages: {
        ClubLabel: string;
        ClubHelpText: string;
        LoadingClubs: string;
        ClubRequiredValidationMessage: string;
    };

    clubSearchUrl: string;
    clubSearchParams: any;
    closeOnSelect: boolean;
    allowClubClear: boolean;

    clubSelectionConfiguration: ClubSelectionConfiguration;

    clubCustomMessages: IClubSelectionCustomMessages;
    validationFields: KnockoutObservable<any>[];
    hasSearchError: KnockoutObservable<boolean>;
    isLoadingClubs: KnockoutObservable<boolean>;
    showClubLabel: KnockoutObservable<boolean>;
    hasLoadedAccountDetails: KnockoutObservable<boolean>;

    selectedClub: KnockoutObservable<number>;
    selectedClubs: KnockoutObservableArray<number>;

    availableClubs: KnockoutObservableArray<SelectTwoWithChildren>;

    isSelectionDisabled: KnockoutComputed<boolean>;
    externalIsPropertyRequired: KnockoutComputed<boolean>;
    externalIsSelectionDisabled: KnockoutComputed<boolean>;

    constructor(params: IClubSelectionParams) {
        // Provided Property Configuration
        this.selectedClub = params.SelectedClub;    // Important:   Should always be left as the default value provided

        this.selectedClubs = params.SelectedClubs;  // Important:   Should always be left as the default value provided
        this.clubSearchUrl = params.ClubSearchUrl;
        this.clubSearchParams = params.ClubSearchParams;
        this.validationFields = params.ValidationFields || [];
        this.clubSelectionConfiguration = params.ClubSelectionConfiguration;
        this.isLoadingClubs = params.IsLoadingClubs || ko.observable(false);
        this.allowClubClear = params.AllowClubClear != null ? params.AllowClubClear : true;
        this.externalIsPropertyRequired = params.ExternalIsPropertyRequired || ko.pureComputed(() => false);
        this.externalIsSelectionDisabled = params.ExternalIsSelectionDisabled || ko.pureComputed(() => false);
        this.showClubLabel = params.ShowClubLabel != null ? ko.observable(params.ShowClubLabel) : ko.observable(true);
        this.clubCustomMessages = params.ClubCustomMessages;

        // Standard Property Configuration
        this.hasSearchError = ko.observable(false);
        this.hasLoadedAccountDetails = ko.observable(false);

        this.availableClubs = ko.observableArray([]);

        this.closeOnSelect = true;

        // Initialisation Methods
        this.retrieveCustomMessages();

        this.setupValidation();
        this.computedMethods();

        // Note: If the configuration hasn't been provided then it doesn't need a deferred load (e.g. waiting on a
        //       subscription to fire off the data load) so configure the hierarchy at component load.
        if (this.clubSelectionConfiguration) {
            this.initialiseSignals();
        } else {
            this.configureClubHierarchy();
        }
    }

    public resetValue(): void {
        if (this.selectedClubs) {
            this.selectedClubs([]);
        }

        if (this.selectedClub) {
            this.selectedClub(null);
        }
    }

    private computedMethods() {
        this.isSelectionDisabled = ko.pureComputed(() => {
            return this.externalIsSelectionDisabled() || this.isLoadingClubs();
        });
    };

    private retrieveCustomMessages() {
        const capitalisedClubTitle = ConfigurableTextService.RetrieveTextSync("DefaultFacilityTitle");

        this.customMessages = {
            ClubLabel: this.clubCustomMessages != null
                ? this.clubCustomMessages.ClubLabel
                : capitalisedClubTitle,
            LoadingClubs: this.clubCustomMessages != null
                ? this.clubCustomMessages.LoadingClubs
                : Deps.LocalisationHelper.build("GenericResources", "LoadingClubs").replace("{0}", ConfigurableTextService.RetrieveTextSync("DefaultFacilitiesTitle")).getString(),
            ClubHelpText: this.clubCustomMessages != null
                ? this.clubCustomMessages.ClubHelpText
                : Deps.LocalisationHelper.build("GenericResources", "ClickToSearchForClub").replace("{0}", ConfigurableTextService.RetrieveTextSync("facilitiesTitle")).getString(),
            ClubRequiredValidationMessage: this.clubCustomMessages != null
                ? this.clubCustomMessages.ClubRequiredValidationMessage
                : Deps.LocalisationHelper.build("ValidationMessageResources", "FieldIsRequired").replace("{name}", capitalisedClubTitle).getString()
        };
    };

    private configureClubHierarchy() {
        this.isLoadingClubs(true);

        Q.all([this.retrieveClubHierarchy(), this.retrieveDefaultClub()])
            .then((response: Array<any>) => {
                const availableClubs = response[0] as Array<ClubLocationSelectorResponse>;
                const homeClubConfiguration = response[1] as HomeClubConfigurationResponse;

                let clubId: number = null;
                if (this.selectedClub) {
                    clubId = this.selectedClub() !== null ? this.selectedClub() : null;
                }

                if (clubId === null) {
                    clubId = homeClubConfiguration !== null && homeClubConfiguration.ClubId !== null
                        ? homeClubConfiguration.ClubId : null;
                }

                const clubHierarchy = this.buildClubHierarchy(availableClubs, clubId);
                this.availableClubs(clubHierarchy);
                this.isLoadingClubs(false);
            });
    };

    private retrieveClubHierarchy(): Q.Promise<Array<ClubLocationSelectorResponse>> {
        this.availableClubs([]);

        this.hasSearchError(true);

        return ClubSelectionService.RetrieveClubHierarchy(this.clubSearchUrl, this.clubSearchParams)
            .then((response: ClubLocationSelectorResponse[]) => {
                return response;
            });
    };

    private retrieveDefaultClub(): Q.Promise<HomeClubConfigurationResponse> {
        return ClubSelectionService.RetrieveDefaultClub()
            .then((response: HomeClubConfigurationResponse) => {
                return response;
            });
    }

    private buildClubHierarchy(clubs: Array<ClubLocationSelectorResponse>, assignedClubId: number): SelectTwoWithChildren[] {
        let clubHierarchy = _.chain(clubs)
            .sortBy("DisplayName")
            .map((clubDetails: ClubLocationSelectorResponse) => {
                let options = <SelectTwoWithChildren>{
                    text: clubDetails.DisplayName ? clubDetails.DisplayName : clubDetails.Name,
                    id: clubDetails.FacilityId.toString()
                };

                if (clubDetails.Children && clubDetails.Children.length > 0) {
                    options.children = this.buildClubHierarchy(clubDetails.Children, assignedClubId);
                } else if (clubDetails.FacilityId == assignedClubId) {
                    options.selected = true;
                    if (this.selectedClub) {
                        this.selectedClub(assignedClubId);
                    } else if (this.selectedClubs) {
                        this.selectedClubs.push(assignedClubId);
                    }
                }

                return options;
            })
            .value();

        return clubHierarchy;
    };

    private setupValidation() {
        if (this.selectedClubs) {
            ValidationService.AddValidationRule(this.selectedClubs, this.validationFields, {
                validation: {
                    validator: () => {
                        return this.selectedClubs().length > 0;
                    },
                    message: this.customMessages.ClubRequiredValidationMessage,
                    onlyIf: () => {
                        return this.externalIsPropertyRequired();
                    }
                }
            });
        }

        if (this.selectedClub) {
            ValidationService.AddValidationRule(this.selectedClub, this.validationFields, {
                required: true,
                message: this.customMessages.ClubRequiredValidationMessage,
                onlyIf: () => {
                    return this.externalIsPropertyRequired();
                }
            });
        };
    };

    private initialiseSignals() {
        this.clubSelectionConfiguration.configureClubHierarchy.add(this.configureClubHierarchy.bind(this));
    };
}